import React, { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import axios from 'axios';
import { ShopCartProvider } from '@/context/ShopCartContext';
import { ShopFavoritesProvider } from '@/context/ShopFavoritesContext';
import { ShopOrgProvider } from '@/context/ShopOrgContext';
import { ShopCustomerAuthProvider } from '@/context/ShopCustomerAuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShopProviders = () => {
  const { orgSlug } = useParams();
  const [org, setOrg] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/public/organization-by-slug/${orgSlug}`)
      .then(async (res) => {
        if (cancelled) return;
        // Footer settings live on the theme, not the org -- fetched once
        // here so every shop page can render the same footer without each
        // page re-fetching the theme. Best-effort: a missing/unpublished
        // theme just means an empty footer, not a broken page.
        const footer = await axios.get(`${API}/public/commerce/${res.data.id}/theme`)
          .then((themeRes) => themeRes.data?.settings?.footer || {})
          .catch(() => ({}));
        if (!cancelled) {
          setOrg({ id: res.data.id, slug: res.data.slug || orgSlug, name: res.data.name, logoUrl: res.data.logo_url, footer });
        }
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      });
    return () => { cancelled = true; };
  }, [orgSlug]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 text-center p-6">
        <p className="text-slate-500">Deze webshop bestaat niet (meer).</p>
      </div>
    );
  }

  if (!org) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-teal-200"></div>
          <p className="text-slate-500">Winkel laden...</p>
        </div>
      </div>
    );
  }

  return (
    <ShopOrgProvider org={org}>
      <ShopCustomerAuthProvider orgId={org.id}>
        <ShopCartProvider orgId={org.id}>
          <ShopFavoritesProvider orgId={org.id}>
            <Outlet />
          </ShopFavoritesProvider>
        </ShopCartProvider>
      </ShopCustomerAuthProvider>
    </ShopOrgProvider>
  );
};

export default ShopProviders;
